import {
  API_ROUTES,
  applyToRecordsResultSchema,
  type ApplyToRecordsRequest,
  lastTimeSchema,
  signOffResultSchema,
  bookkeepingRecordDetailSchema,
  bookkeepingRecordSchema,
  bookkeepingReferenceSchema,
  bookkeepingSettingsSchema,
  bookkeepingStatsSchema,
  type ConfirmRecordRequest,
  type ListBookkeepingQuery,
  type UpdateRecordRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Bookkeeping queries. */

export const bookkeepingKeys = {
  all: (organizationId: string) => ['bookkeeping', organizationId] as const,
  list: (organizationId: string, filters: Partial<ListBookkeepingQuery>) =>
    ['bookkeeping', organizationId, 'list', filters] as const,
  record: (organizationId: string, recordId: string) =>
    ['bookkeeping', organizationId, 'record', recordId] as const,
  stats: (organizationId: string) => ['bookkeeping', organizationId, 'stats'] as const,
} as const;

const pageSchema = z.object({
  items: z.array(bookkeepingRecordSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

function toSearch(filters: Partial<ListBookkeepingQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

export const bookkeepingListQuery = (
  organizationId: string,
  filters: Partial<ListBookkeepingQuery> = {},
) =>
  queryOptions({
    queryKey: bookkeepingKeys.list(organizationId, filters),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.bookkeeping.list(organizationId)}${toSearch(filters)}`,
        schema: pageSchema,
        signal,
      }),
    staleTime: 15_000,
  });

export const bookkeepingRecordQuery = (organizationId: string, recordId: string) =>
  queryOptions({
    queryKey: bookkeepingKeys.record(organizationId, recordId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bookkeeping.byId(organizationId, recordId),
        schema: bookkeepingRecordDetailSchema,
        signal,
      }),
  });

export const bookkeepingStatsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: bookkeepingKeys.stats(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bookkeeping.stats(organizationId),
        schema: bookkeepingStatsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export const bookkeepingSettingsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['bookkeeping', organizationId, 'settings'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bookkeeping.settings(organizationId),
        schema: bookkeepingSettingsSchema,
        signal,
      }),
    staleTime: 300_000,
  });

export const bookkeepingReferenceQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['bookkeeping', organizationId, 'reference'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bookkeeping.reference(organizationId),
        schema: bookkeepingReferenceSchema,
        signal,
      }),
    // Suppliers and accounts change on the timescale of a business, not a
    // screen; refetching them per record would be pure noise.
    staleTime: 300_000,
  });

export async function updateBookkeepingRecord(
  organizationId: string,
  recordId: string,
  body: UpdateRecordRequest,
): Promise<void> {
  await apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.bookkeeping.byId(organizationId, recordId),
    schema: z.undefined(),
    body,
  });
}

export const lastTimeQuery = (organizationId: string, recordId: string) =>
  queryOptions({
    queryKey: ['bookkeeping', organizationId, 'last-time', recordId] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bookkeeping.lastTime(organizationId, recordId),
        schema: lastTimeSchema.nullable(),
        signal,
      }),
    staleTime: 60_000,
  });

export const applyToRecords = (organizationId: string, body: ApplyToRecordsRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.applyMany(organizationId),
    schema: applyToRecordsResultSchema,
    body,
  });

export const confirmManyRecords = (organizationId: string, recordIds: string[]) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.confirmMany(organizationId),
    schema: signOffResultSchema,
    body: { recordIds },
  });

export async function confirmBookkeepingRecord(
  organizationId: string,
  recordId: string,
  body: ConfirmRecordRequest = {},
): Promise<void> {
  await apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.confirm(organizationId, recordId),
    schema: z.undefined(),
    body,
  });
}

export const voidBookkeepingRecord = (organizationId: string, recordId: string, reason: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.void(organizationId, recordId),
    schema: z.undefined(),
    body: { reason },
  });

/** Resolves to the id of the draft copy that now stands in for the record. */
export const correctBookkeepingRecord = (
  organizationId: string,
  recordId: string,
  reason: string,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.correct(organizationId, recordId),
    schema: z.object({ recordId: z.string() }),
    body: { reason },
  });

export const sendRecordBackToReview = (organizationId: string, recordId: string, note?: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bookkeeping.sendBack(organizationId, recordId),
    schema: z.object({ reviewItemId: z.string().nullable() }),
    body: note === undefined ? {} : { note },
  });
